import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../services/authFetch';

const ObatPage = () => {
  const navigate = useNavigate();
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  
  // Check student access
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const userRole = localStorage.getItem('userRole');
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    
    if (userData && isAuthenticated === 'true') {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      // Check if user is student
      if (parsedUser.role !== 'student' && userRole !== 'student') {
        navigate('/');
        return;
      }
    } else {
      navigate('/login');
      return;
    }
  }, [navigate]);
  
  // Date formatting helper function
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day} ${month} ${year}`;
    } catch (error) {
      return '-';
    }
  };

  // Check if medicine is expired or expiring soon
  const getMedicineStatus = (expiredDate) => {
    if (!expiredDate) return 'active';
    
    const today = new Date();
    const expiry = new Date(expiredDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'expired';
    if (diffDays <= 30) return 'expiring';
    return 'active';
  };

  // Fetch medicines from API
  const fetchMedicines = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      const response = await authFetch(`${apiBase}/api/student/medicines`);
      
      if (!response.ok) {
        throw new Error('Gagal memuat data obat');
      }
      
      const result = await response.json();
      // Handle both paginated and direct array responses
      const medicinesData = result.data || result || [];
      
      setMedicines(medicinesData);
    } catch (err) {
      console.error('Error fetching medicines:', err);
      setError(err.message || 'Terjadi kesalahan saat memuat data obat');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch medicines if user is authenticated as student
    const userRole = localStorage.getItem('userRole');
    
    if (user?.role === 'student' || userRole === 'student') {
      fetchMedicines();
    }
  }, [user]);

  // Filter medicines to show only active and expiring (remove expired ones)
  const filteredMedicines = medicines.filter(medicine => {
    const status = getMedicineStatus(medicine.expired);
    // Only show active and expiring medicines
    return status === 'active' || status === 'expiring';
  });

  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={() => navigate('/pelayanan')}>
          <span>←</span> Kembali
        </BackButton>
        <h1>Daftar Obat</h1>
        <p>Catatan daftar obat tersedia</p>
      </PageHeader>
      
      <ContentContainer>
        {loading ? (
          <LoadingState>
            <LoadingIcon>⏳</LoadingIcon>
            <LoadingText>Memuat data obat...</LoadingText>
          </LoadingState>
        ) : error ? (
          <ErrorState>
            <ErrorIcon>⚠️</ErrorIcon>
            <ErrorText>{error}</ErrorText>
            <RetryButton onClick={fetchMedicines}>
              Coba Lagi
            </RetryButton>
          </ErrorState>
        ) : filteredMedicines.length > 0 ? (
          <MedicineList>
            {filteredMedicines.map(medicine => {
              const status = getMedicineStatus(medicine.expired);
              return (
                <MedicineCard key={medicine.id} status={status}>
                  <CardHeader>
                    <MedicineName>{medicine.name}</MedicineName>
                    <StatusBadge status={status}>
                      {status === 'active' ? 'Tersedia' : 
                       status === 'expiring' ? 'Segera Kadaluarsa' : 'Kadaluarsa'}
                    </StatusBadge>
                  </CardHeader>
                  
                  <CardDetails>
                    <DetailItem>
                      <DetailLabel>Tujuan:</DetailLabel>
                      <DetailValue>{medicine.purpose || '-'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Dosis:</DetailLabel>
                      <DetailValue>{medicine.dosage || '-'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Kadaluarsa:</DetailLabel>
                      <DetailValue>{formatDate(medicine.expired)}</DetailValue>
                    </DetailItem>
                    {medicine.instruction && (
                      <NotesBox>
                        <NotesIcon>ℹ️</NotesIcon>
                        <NotesText>{medicine.instruction}</NotesText>
                      </NotesBox>
                    )}
                  </CardDetails>
                </MedicineCard>
              );
            })}
          </MedicineList>
        ) : (
          <EmptyState>
            <EmptyIcon>💊</EmptyIcon>
            <EmptyText>Tidak ada obat tersedia saat ini</EmptyText>
          </EmptyState>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
  position: relative;
  
  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
    text-align: center;
  }
  
  p {
    color: var(--text-light);
    text-align: center;
    font-size: 0.95rem;
  }
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  color: var(--primary);
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  span {
    margin-right: 0.5rem;
    font-size: 1.2rem;
  }
  
  &:hover {
    background-color: var(--primary);
    color: white;
  }
`;

const ContentContainer = styled.div`
  margin-bottom: 2rem;
`;

const LoadingState = styled.div`
  padding: 3rem 1rem;
  text-align: center;
`;

const LoadingIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const LoadingText = styled.p`
  font-size: 1rem;
  color: var(--text-light);
`;

const ErrorState = styled.div`
  padding: 3rem 1rem;
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const ErrorText = styled.p`
  font-size: 1rem;
  color: var(--text-light);
  margin-bottom: 1.5rem;
`;

const RetryButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const MedicineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const MedicineCard = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border-left: 4px solid ${props => {
    switch(props.status) {
      case 'active': return 'var(--primary)';
      case 'expiring': return '#ff9800';
      case 'expired': return '#f44336';
      default: return '#8e9aaf';
    }
  }};
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const CardHeader = styled.div`
  padding: 1rem 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--background-light);
  
  @media (max-width: 768px) {
    padding: 1rem 0.75rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const MedicineName = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  
  ${props => {
    switch(props.status) {
      case 'active':
        return `
          background-color: #e6f8f5;
          color: #00a389;
        `;
      case 'expiring':
        return `
          background-color: #fff3e0;
          color: #ff9800;
        `;
      case 'expired':
        return `
          background-color: #ffebee;
          color: #f44336;
        `;
      default:
        return `
          background-color: #e9ecef;
          color: #6c757d;
        `;
    }
  }}
`;

const CardDetails = styled.div`
  padding: 1rem 1.25rem;
`;

const DetailItem = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
`;

const DetailLabel = styled.span`
  width: 110px;
  font-weight: 500;
  margin-right: 0.5rem;
  color: var(--text-light);
  font-size: 0.9rem;
`;

const DetailValue = styled.span`
  flex: 1;
  color: var(--text-dark);
  font-size: 0.9rem;
`;

const NotesBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.75rem;
  background-color: rgba(43, 93, 161, 0.05);
  border-radius: 8px;
`;

const NotesIcon = styled.div`
  font-size: 1rem;
`;

const NotesText = styled.div`
  font-size: 0.85rem;
  color: var(--text-dark);
  line-height: 1.4;
`;

const EmptyState = styled.div`
  padding: 3rem 1rem;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const EmptyText = styled.p`
  font-size: 1rem;
  color: var(--text-light);
`;

export default ObatPage;